import axios from 'axios'
import { API_BASE_URL } from '@/shared/api/base-url'
import type { TokenPairResponse } from '@/features/auth/domain/auth.types'

// Opaque bearer tokens (see task-to-implement/auth-module/auth.md §4). This module owns token
// storage and the single-flight refresh mutex. It must NOT import http.ts — the only allowed
// dependency direction is http.ts -> auth-tokens.ts, so there is no circular import.

const AUTH_SESSION_STORAGE_KEY = 'joja-next-auth-session'
const REFRESH_ENDPOINT = '/v1/auth/refresh'

type StoredSession = {
  accessToken: string
  refreshToken: string
  accessTokenExpiresAt: string
}

// Access token is mirrored in memory for fast reads on every request.
let session: StoredSession | null = null

const isStoredSession = (value: unknown): value is StoredSession =>
  Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as StoredSession).accessToken === 'string' &&
      typeof (value as StoredSession).refreshToken === 'string',
  )

const loadTokens = (): StoredSession | null => {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as unknown
    if (!isStoredSession(parsed)) {
      window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
      return null
    }

    return parsed
  } catch {
    window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
    return null
  }
}

session = loadTokens()

export const getAccessToken = () => session?.accessToken ?? null
export const getRefreshToken = () => session?.refreshToken ?? null
export const getAccessTokenExpiresAt = () => session?.accessTokenExpiresAt ?? null
export const hasTokens = () => Boolean(session?.refreshToken)

export const setTokens = (pair: TokenPairResponse) => {
  session = {
    accessToken: pair.accessToken,
    refreshToken: pair.refreshToken,
    accessTokenExpiresAt: pair.accessTokenExpiresAt,
  }

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session))
  }
}

export const clearTokens = () => {
  session = null

  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
  }
}

// ---- session-expired hook -------------------------------------------------
// Lets background feature-store calls that fail refresh trigger a redirect to login without
// this module having to know about vue-router or Pinia.
let sessionExpiredHandler: (() => void) | null = null

export const setSessionExpiredHandler = (handler: (() => void) | null) => {
  sessionExpiredHandler = handler
}

const notifySessionExpired = () => {
  sessionExpiredHandler?.()
}

// ---- single-flight refresh mutex ------------------------------------------
// A bare client with NO interceptors — a 401/403 on /auth/refresh must never re-enter the
// refresh path.
const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { Accept: 'application/json' },
})

let refreshPromise: Promise<string | null> | null = null

const performRefresh = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null

  try {
    const { data } = await refreshClient.post<TokenPairResponse>(REFRESH_ENDPOINT, { refreshToken })
    // Persist the ROTATED pair immediately and atomically — the old refresh token is now consumed.
    setTokens(data)
    return data.accessToken
  } catch {
    clearTokens()
    notifySessionExpired()
    return null
  }
}

/**
 * Returns a fresh access token, or null if refresh failed (tokens are then cleared).
 * Concurrent callers share ONE in-flight refresh so the single-use refresh token is never
 * presented twice in parallel (which the backend treats as theft — see auth.md §4.4).
 */
export const refreshAccessToken = (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise

  refreshPromise = performRefresh().finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}
